import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import api, { formatINR, formatApiErrorDetail } from "@/api/client";
import { Users, Store, Package, ShoppingBag, IndianRupee, MessageSquare, ShieldCheck, ShieldOff, Star, Trash2, Search, Plus, Edit, Settings as SettingsIcon, Save, Palette } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/upload/ImageUploader";
import SellerManagement from "@/components/admin/SellerManagement";
import AdminOrders from "@/components/admin/AdminOrders";
import AdminReviews from "@/components/admin/AdminReviews";
import AdminBrands from "@/components/admin/AdminBrands";

const AdminPanel = () => {
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [productModal, setProductModal] = useState(null); // {mode: 'add'|'edit', product?}

  useEffect(() => {
    if (user?.role !== "admin") return;
    refresh();
    // ensure sellers list always available for product modal
    if (sellers.length === 0) api.get("/admin/sellers").then((r) => setSellers(r.data));
  }, [user, tab]);

  const refresh = async () => {
    if (tab === "overview") {
      const { data } = await api.get("/admin/stats"); setStats(data);
    } else if (tab === "sellers") {
      const { data } = await api.get("/admin/sellers"); setSellers(data);
    } else if (tab === "products") {
      const { data } = await api.get(`/admin/products${search ? `?q=${encodeURIComponent(search)}` : ""}`); setProducts(data);
    } else if (tab === "orders") {
      const { data } = await api.get("/admin/orders"); setOrders(data);
    } else if (tab === "users") {
      const { data } = await api.get("/admin/users"); setUsers(data);
    } else if (tab === "contacts") {
      const { data } = await api.get("/admin/contacts"); setContacts(data);
    }
  };

  if (!ready) return <div className="container-fah py-20 text-center text-muted-foreground">Loading…</div>;
  if (!user) return <div className="container-fah py-20 text-center"><Link to="/login" className="text-primary underline">Please login</Link></div>;
  if (user.role !== "admin") {
    return (
      <div className="container-fah py-20 text-center" data-testid="admin-no-access">
        <ShieldOff className="w-12 h-12 mx-auto text-destructive" />
        <h2 className="font-display text-2xl font-bold mt-3">Admin Access Required</h2>
        <p className="text-muted-foreground mt-2">You don't have permission to view this page.</p>
        <Link to="/" className="mt-5 inline-block bg-primary text-white font-semibold py-2.5 px-5 rounded-lg">Go Home</Link>
      </div>
    );
  }

  const verifyToggle = async (id, current) => {
    await api.post(`/admin/sellers/${id}/verify?verified=${!current}`);
    toast.success(current ? "Seller unverified" : "Seller verified ✓");
    refresh();
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product permanently?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product removed");
    refresh();
  };
  const toggleFeature = async (id, current) => {
    await api.post(`/admin/products/${id}/feature?featured=${!current}`);
    toast.success(current ? "Unfeatured" : "Featured ⭐");
    refresh();
  };

  return (
    <div className="container-fah py-6 sm:py-8" data-testid="page-admin">
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 bg-accent text-accent-foreground rounded-full px-3 py-1 text-xs font-semibold mb-2">
          <ShieldCheck className="w-3.5 h-3.5" /> Admin Console
        </div>
        <h1 className="font-display text-3xl font-bold">FarmersAgroHub Admin</h1>
        <p className="text-sm text-muted-foreground">Manage sellers, products, orders & marketplace operations</p>
      </div>

      <div className="flex gap-2 border-b border-border mb-5 overflow-x-auto">
        {[
          ["overview", "Overview"], ["sellers", "Sellers"], ["products", "Products"],
          ["brands", "Brands"],
          ["orders", "Orders"], ["reviews", "Reviews"], ["users", "Users"], ["contacts", "Inquiries"],
          ["settings", "Site Settings"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`admin-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-overview">
          <Kpi icon={Users} color="bg-blue-100 text-blue-700" label="Total Users" value={stats.users} />
          <Kpi icon={Store} color="bg-green-100 text-green-700" label="Sellers" value={stats.sellers.total} sub={`${stats.sellers.verified} verified · ${stats.sellers.pending} pending`} />
          <Kpi icon={Package} color="bg-amber-100 text-amber-700" label="Products" value={stats.products} />
          <Kpi icon={ShoppingBag} color="bg-purple-100 text-purple-700" label="Orders" value={stats.orders.total} sub={`${stats.orders.pending} pending`} />
          <Kpi icon={IndianRupee} color="bg-green-100 text-green-700" label="GMV" value={formatINR(stats.revenue)} />
          <Kpi icon={MessageSquare} color="bg-rose-100 text-rose-700" label="Inquiries" value={stats.contacts} />
        </div>
      )}

      {tab === "sellers" && <SellerManagement />}

      {tab === "brands" && <AdminBrands />}

      {tab === "products" && (
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="flex items-center gap-2 bg-white border border-border rounded-lg px-3 py-2 flex-1 max-w-md">
              <Search className="w-4 h-4 text-muted-foreground" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && refresh()} placeholder="Search products by name / brand…" className="flex-1 text-sm focus:outline-none" data-testid="admin-product-search" />
            </div>
            <button onClick={refresh} className="bg-muted text-foreground text-sm font-semibold px-4 py-2 rounded-lg" data-testid="admin-search-btn">Search</button>
            <button onClick={() => setProductModal({ mode: "add" })} className="bg-secondary text-secondary-foreground text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1 ml-auto" data-testid="admin-btn-add-product">
              <Plus className="w-4 h-4" /> Add Product
            </button>
          </div>
          <div className="card-fah overflow-x-auto" data-testid="admin-products">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>{["Product", "Category", "Seller", "Price", "Stock", "Featured", "Actions"].map((h) => <th key={h} className="px-4 py-3 text-xs uppercase tracking-wide">{h}</th>)}</tr>
              </thead>
              <tbody>
                {products.slice(0, 200).map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`admin-product-${p.id}`}>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><img src={p.images?.[0]} alt="" className="w-9 h-9 rounded object-cover" /><div className="font-medium line-clamp-1 max-w-[200px]">{p.name}</div></div></td>
                    <td className="px-4 py-3 capitalize">{p.category.replace(/-/g, " ")}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{p.seller_name}</td>
                    <td className="px-4 py-3 font-semibold">{formatINR(p.price)}</td>
                    <td className="px-4 py-3">{p.stock}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleFeature(p.id, p.featured)} className={`text-xs ${p.featured ? "text-secondary" : "text-muted-foreground"} hover:scale-110`} data-testid={`btn-feature-${p.id}`}>
                        <Star className={`w-4 h-4 ${p.featured ? "fill-current" : ""}`} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setProductModal({ mode: "edit", product: p })} className="text-primary hover:bg-primary/10 p-1.5 rounded" title="Edit" data-testid={`admin-edit-prod-${p.id}`}><Edit className="w-4 h-4" /></button>
                        <button onClick={() => deleteProduct(p.id)} className="text-destructive hover:bg-destructive/10 p-1.5 rounded" title="Delete" data-testid={`admin-del-prod-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">No products</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "settings" && <SiteSettingsTab />}

      {tab === "orders" && <AdminOrders />}
      {tab === "reviews" && <AdminReviews />}

      {tab === "users" && (
        <div className="card-fah overflow-x-auto" data-testid="admin-users">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>{["Name", "Email", "Phone", "Role", "Joined"].map((h) => <th key={h} className="px-4 py-3 text-xs uppercase tracking-wide">{h}</th>)}</tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-border">
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-xs">{u.email}</td>
                  <td className="px-4 py-3 text-xs">{u.phone || "-"}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full uppercase ${u.role === "admin" ? "bg-destructive/10 text-destructive" : u.role === "seller" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{u.role}</span></td>
                  <td className="px-4 py-3 text-xs">{new Date(u.created_at).toLocaleDateString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "contacts" && (
        <div className="space-y-3" data-testid="admin-contacts">
          {contacts.map((c) => (
            <div key={c.id} className="card-fah p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold">{c.subject}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">From {c.name} ({c.email}) {c.phone && `· ${c.phone}`}</div>
                </div>
                <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString("en-IN")}</div>
              </div>
              <p className="text-sm mt-3 text-foreground/85">{c.message}</p>
            </div>
          ))}
          {contacts.length === 0 && <div className="card-fah p-8 text-center text-muted-foreground">No inquiries yet</div>}
        </div>
      )}

      {productModal && (
        <AdminProductModal
          mode={productModal.mode}
          product={productModal.product}
          sellers={sellers}
          onClose={() => setProductModal(null)}
          onSaved={() => { setProductModal(null); refresh(); }}
        />
      )}
    </div>
  );
};

const Kpi = ({ icon: Icon, color, label, value, sub }) => (
  <div className="card-fah p-4">
    <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-3`}><Icon className="w-5 h-5" /></div>
    <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="font-display text-2xl font-bold mt-0.5">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const CATS = ["seeds", "pesticides", "fungicides", "insecticides", "herbicides", "bio-fertilizers", "plant-growth-promoters", "agriculture-tools", "irrigation-products", "sprayers", "farm-machinery"];

const AdminProductModal = ({ mode, product, sellers, onClose, onSaved }) => {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(
    isEdit ? {
      name: product.name, category: product.category, description: product.description,
      price: String(product.price), mrp: String(product.mrp), stock: String(product.stock),
      unit: product.unit, brand: product.brand, seller_id: product.seller_id,
      featured: !!product.featured,
      images: product.images?.length ? product.images : [],
      variants: product.variants || [],
    } : {
      name: "", category: "seeds", description: "", price: "", mrp: "", stock: "", unit: "", brand: "",
      seller_id: sellers[0]?.id || "", featured: false,
      images: [],
      variants: [],
    }
  );
  const [busy, setBusy] = useState(false);

  const addVariant = () => setForm({ ...form, variants: [...(form.variants || []), { size: "", price: "", mrp: "", stock: "" }] });
  const updateVariant = (i, field, val) => {
    const v = [...form.variants];
    v[i] = { ...v[i], [field]: val };
    setForm({ ...form, variants: v });
  };
  const removeVariant = (i) => setForm({ ...form, variants: form.variants.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.seller_id) { toast.error("Please select a seller"); return; }
    if (!form.images || form.images.length === 0) { toast.error("Please upload at least one product image"); return; }
    setBusy(true);
    try {
      // Clean variants: only keep filled ones, convert types
      const cleanVariants = (form.variants || [])
        .filter((v) => v.size && v.price)
        .map((v) => ({
          size: v.size,
          price: Number(v.price),
          mrp: Number(v.mrp || v.price),
          stock: Number(v.stock || 0),
        }));
      const payload = {
        ...form,
        price: Number(form.price), mrp: Number(form.mrp), stock: Number(form.stock),
        variants: cleanVariants,
      };
      if (isEdit) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(isEdit ? "Product updated" : "Product created");
      onSaved();
    } catch (ex) { toast.error(formatApiErrorDetail(ex.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid={isEdit ? "admin-edit-product-modal" : "admin-add-product-modal"}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-border flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="font-display font-bold text-lg">{isEdit ? "Edit Product" : "Add New Product"}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-lg">✕</button>
        </div>
        <form onSubmit={submit} className="p-5 grid sm:grid-cols-2 gap-3">
          <Lbl label="Seller" cls="sm:col-span-2">
            <select required value={form.seller_id} onChange={(e) => setForm({ ...form, seller_id: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="admin-prod-seller">
              <option value="">-- Select seller --</option>
              {sellers.map((s) => <option key={s.id} value={s.id}>{s.shop_name} — {s.city}</option>)}
            </select>
          </Lbl>
          <Lbl label="Product Name" cls="sm:col-span-2"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="admin-prod-name" /></Lbl>
          <Lbl label="Category">
            <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="admin-prod-category">
              {CATS.map((c) => <option key={c} value={c}>{c.replace(/-/g, " ")}</option>)}
            </select>
          </Lbl>
          <Lbl label="Brand"><input required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="admin-prod-brand" /></Lbl>
          <Lbl label="Base Selling Price ₹"><input required type="number" min="1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="admin-prod-price" /></Lbl>
          <Lbl label="Base MRP ₹"><input required type="number" min="1" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="admin-prod-mrp" /></Lbl>
          <Lbl label="Default Stock"><input required type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="admin-prod-stock" /></Lbl>
          <Lbl label="Default Size / Unit (e.g. 1kg, 500ml)"><input required value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="admin-prod-unit" /></Lbl>
          <Lbl label="Description" cls="sm:col-span-2"><textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="admin-prod-desc" /></Lbl>
          <Lbl label="Product Images" cls="sm:col-span-2">
            <ImageUploader value={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} maxFiles={10} />
          </Lbl>

          {/* SIZE VARIANTS */}
          <div className="sm:col-span-2 border-t border-border pt-4 mt-2">
            <div className="flex items-center justify-between mb-2">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-primary">📦 Size Variants (Optional)</label>
                <p className="text-[11px] text-muted-foreground">अलग-अलग size के लिए अलग price/stock। खाली छोड़ें तो सिर्फ Default Size दिखेगा।</p>
              </div>
              <button type="button" onClick={addVariant} className="text-xs bg-primary text-white font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1" data-testid="add-variant-btn">
                <Plus className="w-3.5 h-3.5" /> Add Size
              </button>
            </div>
            {(form.variants || []).length === 0 && (
              <div className="text-center py-4 text-xs text-muted-foreground border border-dashed border-border rounded-lg">No size variants. Click "Add Size" to add 1kg / 5kg / 10kg etc.</div>
            )}
            {(form.variants || []).map((v, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 mb-2 items-center" data-testid={`variant-${i}`}>
                <input placeholder="Size (e.g. 1kg)" value={v.size} onChange={(e) => updateVariant(i, "size", e.target.value)} className="px-2 py-1.5 border border-border rounded text-xs bg-white" data-testid={`variant-size-${i}`} />
                <input placeholder="Price ₹" type="number" value={v.price} onChange={(e) => updateVariant(i, "price", e.target.value)} className="px-2 py-1.5 border border-border rounded text-xs bg-white" data-testid={`variant-price-${i}`} />
                <input placeholder="MRP ₹" type="number" value={v.mrp} onChange={(e) => updateVariant(i, "mrp", e.target.value)} className="px-2 py-1.5 border border-border rounded text-xs bg-white" data-testid={`variant-mrp-${i}`} />
                <input placeholder="Stock" type="number" value={v.stock} onChange={(e) => updateVariant(i, "stock", e.target.value)} className="px-2 py-1.5 border border-border rounded text-xs bg-white" data-testid={`variant-stock-${i}`} />
                <button type="button" onClick={() => removeVariant(i)} className="text-destructive hover:bg-destructive/10 p-1.5 rounded" data-testid={`del-variant-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>

          <label className="sm:col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="admin-prod-featured" />
            <span>Featured product (show on homepage)</span>
          </label>
          <button disabled={busy} type="submit" className="sm:col-span-2 bg-primary text-white font-semibold py-2.5 rounded-lg disabled:opacity-50" data-testid="admin-btn-save-product">{busy ? "Saving…" : isEdit ? "Save Changes" : "Add Product"}</button>
        </form>
      </div>
    </div>
  );
};

const Lbl = ({ label, cls = "", children }) => (
  <div className={cls}>
    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const SiteSettingsTab = () => {
  const { settings, refresh } = useSiteSettings();
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => { setForm(settings); }, [settings]);
  useEffect(() => { api.get("/admin/products").then((r) => setProducts(r.data)); }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/site-settings", form);
      await refresh();
      toast.success("Site settings saved");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const toggleFeatured = (pid) => {
    const ids = form.featured_product_ids || [];
    const next = ids.includes(pid) ? ids.filter((x) => x !== pid) : [...ids, pid];
    setForm({ ...form, featured_product_ids: next.slice(0, 12) });
  };

  return (
    <div className="space-y-5" data-testid="admin-settings">
      <Section icon={SettingsIcon} title="Brand Identity">
        <Field label="Site Name"><input value={form.site_name || ""} onChange={onChange("site_name")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-site-name" /></Field>
        <Field label="Main Tagline"><input value={form.tagline || ""} onChange={onChange("tagline")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-tagline" /></Field>
        <Field label="Short Tagline (header)"><input value={form.short_tagline || ""} onChange={onChange("short_tagline")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-short-tagline" /></Field>
        <Field label="Logo URL">
          <input value={form.logo_url || ""} onChange={onChange("logo_url")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-logo-url" placeholder="https://… upload your logo & paste URL" />
          {form.logo_url && <img src={form.logo_url} alt="logo" className="mt-2 w-16 h-16 object-contain border border-border rounded-lg bg-muted/40 p-1" />}
        </Field>
      </Section>

      <Section icon={Palette} title="Theme Colors">
        <Field label="Primary Color (header, buttons)">
          <div className="flex gap-2 items-center">
            <input type="color" value={form.primary_color || "#1B5E20"} onChange={onChange("primary_color")} className="h-10 w-14 rounded border border-border cursor-pointer" data-testid="set-primary-color" />
            <input value={form.primary_color || ""} onChange={onChange("primary_color")} className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-white font-mono" />
          </div>
        </Field>
        <Field label="Accent Color (CTA buttons, badges)">
          <div className="flex gap-2 items-center">
            <input type="color" value={form.accent_color || "#F57F17"} onChange={onChange("accent_color")} className="h-10 w-14 rounded border border-border cursor-pointer" data-testid="set-accent-color" />
            <input value={form.accent_color || ""} onChange={onChange("accent_color")} className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-white font-mono" />
          </div>
        </Field>
      </Section>

      <Section icon={SettingsIcon} title="Homepage Hero">
        <Field label="Eyebrow Badge"><input value={form.hero_eyebrow || ""} onChange={onChange("hero_eyebrow")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-hero-eyebrow" /></Field>
        <Field label="Hero Title — Line 1"><input value={form.hero_title_line1 || ""} onChange={onChange("hero_title_line1")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-hero-line1" /></Field>
        <Field label="Highlighted Word (orange)"><input value={form.hero_title_highlight || ""} onChange={onChange("hero_title_highlight")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-hero-highlight" /></Field>
        <Field label="Hero Title — Line 2"><input value={form.hero_title_line2 || ""} onChange={onChange("hero_title_line2")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-hero-line2" /></Field>
        <Field label="Hero Subtitle"><textarea rows={3} value={form.hero_subtitle || ""} onChange={onChange("hero_subtitle")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-hero-sub" /></Field>
        <Field label="Hero Background Image URL"><input value={form.hero_image || ""} onChange={onChange("hero_image")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-hero-image" />
          {form.hero_image && <img src={form.hero_image} alt="" className="mt-2 w-full max-w-md h-32 object-cover rounded-lg border border-border" />}
        </Field>
        <Field label="CTA Button Text"><input value={form.hero_cta_text || ""} onChange={onChange("hero_cta_text")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-hero-cta" /></Field>
      </Section>

      <Section icon={SettingsIcon} title="Contact Details">
        <Field label="Contact Email"><input type="email" value={form.contact_email || ""} onChange={onChange("contact_email")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-email" /></Field>
        <Field label="Contact Phone (display)"><input value={form.contact_phone || ""} onChange={onChange("contact_phone")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-phone" /></Field>
        <Field label="WhatsApp Number (digits only, e.g. 918090245875)"><input value={form.whatsapp_number || ""} onChange={onChange("whatsapp_number")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white font-mono" data-testid="set-whatsapp" /></Field>
        <Field label="Office Address"><textarea rows={2} value={form.contact_address || ""} onChange={onChange("contact_address")} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="set-address" /></Field>
      </Section>

      <Section icon={Star} title={`Featured Products (${(form.featured_product_ids || []).length} selected · max 12)`}>
        <p className="text-xs text-muted-foreground mb-2">Click on products to feature them on the homepage. If none selected, top-rated products are shown automatically.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-96 overflow-y-auto p-1">
          {products.map((p) => {
            const sel = (form.featured_product_ids || []).includes(p.id);
            return (
              <button type="button" key={p.id} onClick={() => toggleFeatured(p.id)} className={`text-left card-fah p-2 hover-lift transition ${sel ? "border-2 border-secondary bg-accent/40" : ""}`} data-testid={`feat-toggle-${p.id}`}>
                <img src={p.images?.[0]} alt="" className="w-full aspect-square object-cover rounded mb-1" />
                <div className="text-xs font-semibold line-clamp-2">{p.name}</div>
                <div className="text-[10px] text-muted-foreground">{p.seller_name}</div>
                {sel && <div className="text-[10px] mt-1 text-secondary font-bold">★ FEATURED</div>}
              </button>
            );
          })}
        </div>
      </Section>

      <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border -mx-4 px-4 py-3 flex justify-end gap-2 z-10">
        <button onClick={() => setForm(settings)} className="px-4 py-2 border border-border rounded-lg text-sm font-semibold">Reset</button>
        <button onClick={save} disabled={busy} className="bg-primary text-white px-5 py-2 rounded-lg font-bold flex items-center gap-1 disabled:opacity-50" data-testid="admin-save-settings">
          <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="card-fah p-5">
    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
      <Icon className="w-5 h-5 text-primary" />
      <h3 className="font-display font-bold">{title}</h3>
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default AdminPanel;
